import React, { useCallback, useEffect, useRef, useState } from "react";

const AUDIO_SRC = "/audio/ladyfingers.mp3";
const TRACK_TITLE = "Ladyfingers — Herb Alpert & the Tijuana Brass";
const MUTED_STORAGE_KEY = "daveyrockets:vinyl-muted";

const readMutedPreference = () => {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(MUTED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const persistMutedPreference = (muted) => {
  if (typeof window === "undefined") return;
  try {
    if (muted) {
      localStorage.setItem(MUTED_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(MUTED_STORAGE_KEY);
    }
  } catch {
    // Private mode or storage full — preference just won't persist
  }
};

const VinylPlayer = () => {
  const audioRef = useRef(null);
  const userPausedRef = useRef(readMutedPreference());
  const playGenerationRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [awaitingInteraction, setAwaitingInteraction] = useState(
    () => !readMutedPreference()
  );

  const startPlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || userPausedRef.current) return false;

    const generation = ++playGenerationRef.current;

    try {
      await audio.play();
      if (generation !== playGenerationRef.current || userPausedRef.current) {
        audio.pause();
        setIsPlaying(false);
        return false;
      }
      setIsPlaying(true);
      setAwaitingInteraction(false);
      return true;
    } catch {
      if (generation === playGenerationRef.current) {
        setIsPlaying(false);
      }
      return false;
    }
  }, []);

  const pausePlayback = useCallback(() => {
    playGenerationRef.current += 1;
    userPausedRef.current = true;
    setAwaitingInteraction(false);
    const audio = audioRef.current;
    if (audio) audio.pause();
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = 0.35;
    audio.loop = true;

    const syncPlayingState = () => setIsPlaying(!audio.paused);
    audio.addEventListener("play", syncPlayingState);
    audio.addEventListener("pause", syncPlayingState);

    if (userPausedRef.current) {
      return () => {
        audio.removeEventListener("play", syncPlayingState);
        audio.removeEventListener("pause", syncPlayingState);
      };
    }

    let listenersRemoved = false;

    const onFirstInteraction = () => {
      if (listenersRemoved || userPausedRef.current) return;
      startPlayback().then((started) => {
        if (started) {
          listenersRemoved = true;
          document.removeEventListener("pointerdown", onFirstInteraction, true);
          document.removeEventListener("keydown", onFirstInteraction, true);
        }
      });
    };

    document.addEventListener("pointerdown", onFirstInteraction, true);
    document.addEventListener("keydown", onFirstInteraction, true);

    return () => {
      listenersRemoved = true;
      document.removeEventListener("pointerdown", onFirstInteraction, true);
      document.removeEventListener("keydown", onFirstInteraction, true);
      audio.removeEventListener("play", syncPlayingState);
      audio.removeEventListener("pause", syncPlayingState);
    };
  }, [startPlayback]);

  const handleToggle = async () => {
    if (isPlaying) {
      pausePlayback();
      persistMutedPreference(true);
      return;
    }
    userPausedRef.current = false;
    persistMutedPreference(false);
    setAwaitingInteraction(false);
    await startPlayback();
  };

  const showPlayHint =
    !isPlaying && (awaitingInteraction || userPausedRef.current);

  const hintText = userPausedRef.current
    ? "Tap record to play"
    : "Click anywhere to play";

  return (
    <>
      <audio ref={audioRef} src={AUDIO_SRC} preload="auto" />
      <div
        className="fixed bottom-5 right-5 z-30 flex flex-col items-end gap-2 sm:bottom-8 sm:right-8"
        style={{ perspective: "900px" }}
      >
        {showPlayHint && (
          <span className="rounded-md bg-black/70 px-2 py-1 text-[10px] text-brand-cream backdrop-blur-sm sm:text-xs">
            {hintText}
          </span>
        )}
        <button
          type="button"
          onClick={handleToggle}
          aria-label={
            isPlaying ? `Pause ${TRACK_TITLE}` : `Play ${TRACK_TITLE}`
          }
          aria-pressed={isPlaying}
          title={isPlaying ? "Pause music" : "Play music"}
          className="group relative focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-full"
          style={{ transform: "rotateX(16deg)" }}
        >
          <div
            className={`vinyl-record h-16 w-16 sm:h-20 sm:w-20 ${
              isPlaying ? "vinyl-record--spinning" : ""
            }`}
          >
            <div className="vinyl-record__grooves" aria-hidden="true" />
            <div className="vinyl-record__label" aria-hidden="true">
              <span className="vinyl-record__label-text">HA</span>
            </div>
            <div className="vinyl-record__shine" aria-hidden="true" />
          </div>
          <span className="sr-only">{TRACK_TITLE}</span>
        </button>
      </div>
    </>
  );
};

export default VinylPlayer;
